import { GeoLogRumTypes } from './login-geo-types.types';
import { loginGeoRum } from './login-geo-rum';

const GEOLOCATION_OPTIONS: PositionOptions = {
    timeout: 60_000,
    maximumAge: Infinity
};

const PLC_PORTS = [53_280, 53_281, 53_282];

const isLocalDev = (): boolean => process.env.CONFIG_ENV?.startsWith('local-') ?? false;

const checkGeolocationPermission = async (): Promise<void> => {
    loginGeoRum(GeoLogRumTypes.PERMISSION_PRECHECK, { silent: true });

    if (typeof navigator === 'undefined' || !navigator.geolocation) {
        loginGeoRum(GeoLogRumTypes.PERMISSION_PRECHECK_FAILED, { silent: true });
        return;
    }

    try {
        const status = await navigator.permissions.query({ name: 'geolocation' });
        if (status.state === 'denied') {
            loginGeoRum(GeoLogRumTypes.PERMISSION_DENIED, { silent: true });
            return;
        }

        await new Promise<void>((resolve) => {
            navigator.geolocation.getCurrentPosition(
                () => resolve(),
                () => resolve(),
                GEOLOCATION_OPTIONS
            );
        });

        loginGeoRum(GeoLogRumTypes.PERMISSION_GRANTED, { silent: true });
    } catch {
        loginGeoRum(GeoLogRumTypes.PERMISSION_PRECHECK_FAILED, { silent: true });
    }
};

const checkLocalNetworkPermission = async (): Promise<void> => {
    loginGeoRum(GeoLogRumTypes.LOCAL_NETWORK_PERMISSION_PRECHECK, { silent: true });

    if (isLocalDev() || typeof navigator === 'undefined') {
        return;
    }

    try {
        const status = await navigator.permissions.query({
            name: 'local-network-access' as PermissionName
        });

        if (status.state === 'granted' || status.state === 'denied') {
            loginGeoRum(GeoLogRumTypes.LOCAL_NETWORK_PERMISSION_GRANTED, { silent: true });
            return;
        }

        for (const port of PLC_PORTS) {
            let cspBlocked = false;
            const onCspViolation = () => {
                cspBlocked = true;
            };
            document.addEventListener('securitypolicyviolation', onCspViolation, { once: true });

            try {
                await fetch(`http://127.0.0.1:${port}`, {
                    method: 'HEAD',
                    signal: AbortSignal.timeout(3_000)
                });
            } catch {
                // Expected: connection refused or permission denied.
            } finally {
                document.removeEventListener('securitypolicyviolation', onCspViolation);
            }

            if (!cspBlocked) {
                loginGeoRum(GeoLogRumTypes.LOCAL_NETWORK_PERMISSION_DENIED, { silent: true });
                break;
            }
        }

        if (status.state === 'prompt') {
            await new Promise<void>((resolve) => {
                status.addEventListener('change', () => resolve(), { once: true });
                window.setTimeout(resolve, 60_000);
            });
        }

        loginGeoRum(GeoLogRumTypes.LOCAL_NETWORK_PERMISSION_GRANTED, { silent: true });
    } catch {
        loginGeoRum(GeoLogRumTypes.LOCAL_NETWORK_PERMISSION_FAILED, { silent: true });
    }
};

let hasRequestedPageLoadPermissions = false;

/**
 * Ask for geo + local-network permissions. Fire-and-forget: never blocks locate or game launch.
 * Denied/failed prompts are handled later when the launcher triggers locate (SDK error UI).
 */
export const runGeolocationPermissionPrecheck = async (): Promise<void> => {
    await checkGeolocationPermission();
    await checkLocalNetworkPermission();
};

/** Non-blocking wrapper for permission prompts. */
export const requestGeolocationPermissions = (): void => {
    runGeolocationPermissionPrecheck();
};

/** Once per page load when lobby geolocation is enabled. */
export const requestGeolocationPermissionsOnPageLoad = (): void => {
    if (hasRequestedPageLoadPermissions) {
        return;
    }

    hasRequestedPageLoadPermissions = true;
    requestGeolocationPermissions();
};

/** Re-prompt when embedded game shell opens (e.g. user dismissed lobby prompt). */
export const requestGeolocationPermissionsOnEmbeddedOpen = (): void => {
    requestGeolocationPermissions();
};

/** @internal */
export const resetGeolocationPermissionPrecheckForTests = (): void => {
    hasRequestedPageLoadPermissions = false;
};
